<template>
  <v-card outlined class="building-plan" :class="{ 'building-plan--full-window': fullWindow }">
    <div class="plan-toolbar">
      <v-select class="plan-location" :items="regionOptions" item-text="text" item-value="value" v-model="region" :label="$t('building.plan.location')" :menu-props="{ zIndex: 220 }" outlined dense hide-details @change="changeRegion" />
      <v-select class="plan-status" :items="statusOptions" v-model="status" :label="$t('building.plan.status')" :menu-props="{ zIndex: 220 }" outlined dense hide-details />
      <div class="plan-search" v-click-outside="closeSearch">
        <v-text-field ref="searchField" v-model="search" :label="$t('building.plan.searchAction')" :aria-label="$t('building.plan.search')" clearable dense hide-details
          role="combobox" aria-autocomplete="list" :aria-expanded="String(showSearchResults)" aria-controls="plan-search-results" :aria-activedescendant="showSearchResults && searchResults.length ? 'plan-search-' + searchIndex : undefined"
          @focus="searchOpen = true" @input="searchOpen = true; searchIndex = 0" @keydown="navigateSearch" />
        <ul v-if="showSearchResults" id="plan-search-results" class="plan-search-results" role="listbox">
          <li v-for="(entry, index) in searchResults" :id="'plan-search-' + index" :key="entry.key" role="option" :aria-selected="String(index === searchIndex)">
            <button type="button" @click="chooseSearchResult(entry)" @mouseenter="searchIndex = index"><strong>{{ entry.place_code }}</strong><small>{{ entry.locationName }}</small><span>{{ entry.active.map(person => person.name).join(' · ') }}</span></button>
          </li>
          <li v-if="!searchResults.length" class="plan-search-empty">{{ $t('building.plan.noResults') }}</li>
        </ul>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }"><v-btn class="plan-search-button" icon v-bind="attrs" v-on="on" :disabled="loading" :aria-label="$t('building.plan.searchAction')" @click="submitSearch"><v-icon>mdi-magnify</v-icon></v-btn></template>
          {{ $t('building.plan.searchAction') }}
        </v-tooltip>
      </div>
      <v-btn-toggle v-model="viewMode" mandatory class="plan-view-toggle">
        <v-tooltip bottom v-for="view in views" :key="view.id">
          <template v-slot:activator="{ on, attrs }"><v-btn :value="view.id" small v-bind="attrs" v-on="on" :aria-label="view.label"><v-icon small>{{ view.icon }}</v-icon></v-btn></template>
          {{ view.label }}
        </v-tooltip>
      </v-btn-toggle>
      <span class="plan-result-count" role="status">{{ $t('building.plan.results', { count: matches.length, total: workplaces.length }) }}</span>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }"><v-btn ref="windowToggle" class="plan-window-toggle" icon v-bind="attrs" v-on="on" :aria-label="$t('building.plan.' + (fullWindow ? 'exitFullWindow' : 'fullWindow'))" :aria-pressed="String(fullWindow)" @click="toggleFullWindow"><v-icon>{{ fullWindow ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon></v-btn></template>
        {{ $t('building.plan.' + (fullWindow ? 'exitFullWindow' : 'fullWindow')) }}
      </v-tooltip>
    </div>
    <v-alert v-if="error" text dense type="error" role="alert">{{ $t('building.plan.loadError') }}<v-btn small text @click="load">{{ $t('building.plan.retry') }}</v-btn></v-alert>
    <v-alert v-if="geometryError" text dense type="warning">{{ $t('building.plan.geometryError') }}</v-alert>
    <v-alert v-if="missingHash" text dense type="info">{{ $t('building.plan.unknownDesk', { code: missingHash }) }}</v-alert>
    <div class="plan-layout">
      <div class="plan-main">
        <div v-show="viewMode === 'map' && region !== 'unmapped'" class="plan-map-area" :class="{ 'plan-map-area--expanded': mapHeightExpanded }">
          <div class="plan-map-tools">
            <v-tooltip bottom v-for="tool in mapTools" :key="tool.id">
              <template v-slot:activator="{ on, attrs }"><v-btn icon small v-bind="attrs" v-on="on" :aria-label="tool.label" :disabled="!geometry.geometries.length" @click="mapAction(tool.id)"><v-icon small>{{ tool.icon }}</v-icon></v-btn></template>
              {{ tool.label }}
            </v-tooltip>
          </div>
          <div ref="stage" class="plan-map-stage" :class="{ 'plan-map-stage--building': region === 'all', 'plan-map-stage--expanded': mapHeightExpanded }" :style="{ '--plan-aspect': regionBounds[2] + ' / ' + regionBounds[3] }" @pointerdown.capture="pointerDown" @pointermove.capture="pointerMove" @wheel="zoomWheel">
            <PlanMap ref="map" :entries="mapPresentation" :bounds="regionBounds" :background="geometry.background" :admin="isAdmin"
              @activate="selectFromMap" @hover="hoverDesk" @unhover="clearHoveredDesk" @navigate="navigateMap" />
            <PlanDeskPreview :workplace="hoveredWorkplace" :admin="isAdmin" :endpoint="endpoint" :point="hoverPoint" :selectable="mobilePreview" @select="openPreviewDesk" @close="clearHoveredDesk(hoveredId)" />
            <div v-if="loading" class="plan-loading"><v-progress-circular indeterminate color="primary" :aria-label="$t('building.plan.loading')" /></div>
            <div v-else-if="!error && !matches.length" class="plan-map-empty" role="status">{{ $t('building.plan.noResults') }}</div>
          </div>
          <div class="plan-legend-tool">
            <v-menu v-model="legendOpen" top left offset-y :close-on-content-click="false" min-width="260" max-width="320" :z-index="220">
              <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }"><v-btn icon small v-bind="{ ...menuAttrs, ...tooltipAttrs }" v-on="{ ...menuOn, ...tooltipOn, keydown: [menuOn.keydown, tooltipOn.keydown] }" :aria-label="$t('building.plan.legend')" :aria-expanded="String(legendOpen)" aria-controls="plan-legend"><v-icon small>mdi-map-legend</v-icon></v-btn></template>
                  {{ $t('building.plan.legend') }}
                </v-tooltip>
              </template>
              <div id="plan-legend" class="plan-legend" :aria-label="$t('building.plan.legend')">
                <h3>{{ $t('building.plan.legend') }}</h3>
                <span v-for="item in legend" :key="item.key"><i :style="{ background: item.color }" />{{ item.label }}</span>
                <span v-if="isAdmin"><i class="legend-dot" style="background:#8250ff" />{{ $t('building.plan.photoPending') }}</span>
                <span v-if="isAdmin"><i class="legend-dot" style="background:#50a3ff" />{{ $t('building.plan.bioPending') }}</span>
                <span><b class="legend-shared" aria-hidden="true">2</b>{{ $t('building.plan.shared') }}</span>
                <div v-if="isAdmin" class="plan-expiry-legend">
                  <h4>{{ $t('building.plan.fullLegend') }}</h4>
                  <span v-for="band in expiryBands" :key="band.key"><i :style="{ background: band.color }" />{{ $t('building.plan.bands.' + band.key) }}</span>
                </div>
              </div>
            </v-menu>
          </div>
        </div>
        <div v-if="viewMode === 'list' || region === 'unmapped'" class="plan-list-area">
          <v-progress-linear v-if="loading" indeterminate color="primary" />
          <v-simple-table fixed-header height="620px" class="plan-results-table">
            <thead><tr><th>{{ $t('building.plan.desk') }}</th><th>{{ $t('building.plan.assignedPeople') }}</th><th>{{ $t('building.plan.status') }}</th><th v-if="isAdmin">{{ $t('building.plan.pending') }}</th></tr></thead>
            <tbody>
              <tr v-for="entry in matches" :key="entry.key" tabindex="0" :aria-selected="String(entry.key === selectedId)" :class="{ 'plan-result--selected': entry.key === selectedId }"
                @click="selectDesk(entry, true)" @keydown.enter.prevent="selectDesk(entry, true)" @keydown.space.prevent="selectDesk(entry, true)">
                <td><strong>{{ entry.place_code }}</strong><small>{{ entry.locationName }}</small></td>
                <td><span v-if="!entry.active.length">{{ $t('building.plan.noAssignments') }}</span><span v-for="assignment in entry.active" :key="assignment.id" class="result-person">{{ assignment.name }}</span></td>
                <td><i class="result-status-dot" :style="{ background: color(entry) }" />{{ $t('building.plan.states.' + entry.state) }}</td>
                <td v-if="isAdmin"><span v-if="entry.photoPending" class="result-pending"><i class="legend-dot" style="background:#8250ff" />{{ entry.photoPending }}</span><span v-if="entry.bioPending" class="result-pending"><i class="legend-dot" style="background:#50a3ff" />{{ entry.bioPending }}</span></td>
              </tr>
              <tr v-if="!loading && !matches.length"><td :colspan="isAdmin ? 4 : 3" class="plan-list-empty">{{ $t('building.plan.noResults') }}</td></tr>
            </tbody>
          </v-simple-table>
        </div>
      </div>
      <WorkplaceDetail :workplace="selectedWorkplace" :admin="isAdmin" :endpoint="endpoint" @close="clearSelection" @focus="focusSelected" @share="copyLink" @copy="copyText" />
    </div>
    <v-snackbar v-model="copyNotice" :timeout="1800">{{ $t('building.plan.copied') }}</v-snackbar>
  </v-card>
</template>

<script>
import axios from 'axios';
import Panzoom from '@panzoom/panzoom';
import PlanMap from '@/components/building/PlanMap';
import WorkplaceDetail from '@/components/building/WorkplaceDetail';
import PlanDeskPreview from '@/components/building/PlanDeskPreview';
import { PLAN_REGIONS, readPlanGeometry, isInRegion } from '@/components/building/planGeometry';
import { deskKey, normalizeWorkplace, matchesWorkplace, deskColor, isActiveAssignmentForPerson } from '@/components/building/planModel';

export default {
  name: 'GeneralPlan', components: { PlanMap, WorkplaceDetail, PlanDeskPreview },
  data: () => ({
    rawWorkplaces: [], geometry: { background: '', geometries: [] }, loading: true, error: false, geometryError: false, initialized: false,
    region: 'all', search: '', searchOpen: false, searchIndex: 0, status: 'all', selectedId: '', hoveredId: '', suppressedHoverId: '', hoverPoint: null, mobilePreview: false, missingHash: '', viewMode: 'map', mapHeightExpanded: false, legendOpen: false, copyNotice: false, fullWindow: false, previousBodyOverflow: null, today: new Date()
  }),
  computed: {
    endpoint() { return this.$store.state.endpoint.replace(/\/$/, ''); },
    geometryById() { return Object.fromEntries(this.geometry.geometries.map(geometry => [deskKey(geometry.id), geometry])); },
    workplaces() {
      return this.rawWorkplaces.map(raw => {
        const workplace = normalizeWorkplace(raw, this.isAdmin, this.today);
        const geometry = this.geometryById[workplace.key];
        const region = geometry ? geometry.region : 'unmapped';
        const room = (workplace.place_code.match(/^[a-z]+\d+/i) || [workplace.place_code.split(/[-_]/)[0]])[0];
        return { ...workplace, geometry, region, room, locationName: this.regionName(region) };
      }).sort((a, b) => a.place_code.localeCompare(b.place_code, this.$i18n.locale, { numeric: true }));
    },
    workplacesById() { return Object.fromEntries(this.workplaces.map(workplace => [workplace.key, workplace])); },
    mapEntries() {
      return this.geometry.geometries.map(geometry => this.workplacesById[deskKey(geometry.id)] || {
        ...normalizeWorkplace({ place_code: geometry.id.toUpperCase() }, this.isAdmin, this.today), geometry,
        region: geometry.region, room: '', locationName: this.regionName(geometry.region)
      });
    },
    visibleMapEntries() { return this.mapEntries.filter(entry => isInRegion(this.region, entry.region)); },
    renderedMapEntries() {
      const layer = entry => entry.key === this.selectedId ? 1 : 0;
      return this.visibleMapEntries.slice().sort((a, b) => layer(a) - layer(b));
    },
    mapPresentation() {
      return this.renderedMapEntries.map(workplace => ({
        workplace, color: this.color(workplace), label: this.deskLabel(workplace),
        selected: workplace.key === this.selectedId, dimmed: this.isDeskDimmed(workplace)
      }));
    },
    matches() {
      return this.workplaces.filter(workplace => (this.searchActive || isInRegion(this.region, workplace.region)) &&
        matchesWorkplace(workplace, this.search, this.status, this.isAdmin));
    },
    searchActive() { return !!String(this.search || '').trim(); },
    showSearchResults() { return this.searchOpen && this.searchActive; },
    searchResults() { return this.matches.slice(0, 12); },
    matchingIds() { return new Set(this.matches.map(workplace => workplace.key)); },
    selectedWorkplace() { return this.workplacesById[this.selectedId] || this.mapEntries.find(entry => entry.key === this.selectedId) || null; },
    hoveredWorkplace() { return this.mapEntries.find(entry => entry.key === this.hoveredId) || null; },
    regionOptions() {
      const regions = PLAN_REGIONS.map(region => ({ value: region.id, text: this.regionName(region.id) }));
      if (this.workplaces.some(workplace => !workplace.geometry)) regions.push({ value: 'unmapped', text: this.regionName('unmapped') });
      return regions;
    },
    regionBounds() { return (PLAN_REGIONS.find(region => region.id === this.region) || PLAN_REGIONS[0]).bounds; },
    statusOptions() { return ['all', 'occupied', 'free', 'shared', ...(this.isAdmin ? ['expired', 'expiring', 'photoPending', 'bioPending'] : [])].map(value => ({ value, text: this.$t('building.plan.states.' + value) })); },
    views() { return [{ id: 'map', icon: 'mdi-floor-plan', label: this.$t('building.plan.map') }, { id: 'list', icon: 'mdi-format-list-bulleted', label: this.$t('building.plan.list') }]; },
    mapTools() { return [{ id: 'out', icon: 'mdi-minus', label: this.$t('building.plan.zoomOut') }, { id: 'in', icon: 'mdi-plus', label: this.$t('building.plan.zoomIn') }, { id: 'fit', icon: 'mdi-fit-to-screen-outline', label: this.$t('building.plan.fit') }]; },
    expiryBands() { return [{ key: 'indefinite', color: '#14d100' }, { key: 'underOne', color: '#a2ff64' }, { key: 'one', color: '#8bdb56' }, { key: 'two', color: '#75b848' }, { key: 'three', color: '#5e943a' }, { key: 'four', color: '#47702c' }]; },
    legend() {
      const items = [{ key: 'free', color: '#ffffff' }, { key: this.isAdmin ? 'current' : 'occupied', color: this.isAdmin ? '#a2ff64' : '#c4b7c8' }];
      if (this.isAdmin) items.push({ key: 'expiring', color: '#ffdd09' }, { key: 'expired', color: '#ff6464' });
      return items.map(item => ({ ...item, label: this.$t('building.plan.states.' + item.key) }));
    }
  },
  watch: {
    '$route.fullPath'() { if (this.initialized) this.syncRoute(); },
    viewMode(value) {
      this.legendOpen = false;
      this.hoveredId = ''; this.hoverPoint = null;
      this.mapHeightExpanded = value === 'map' && this.selectedWorkplace && this.canExpandToDetail(this.region);
      if (value === 'map') this.$nextTick(() => {
        if (!this.panzoom) return;
        this.panzoom.reset({ animate: false });
      });
    },
    region() { this.legendOpen = false; this.hoveredId = ''; }
  },
  mounted() {
    this.cancelSource = axios.CancelToken.source(); this.load();
    this.$nextTick(this.updateViewportSpace);
    window.addEventListener('resize', this.updateViewportSpace);
    window.addEventListener('keydown', this.exitFullWindowOnEscape);
    this.dayTimer = window.setInterval(() => { this.today = new Date(); }, 60000);
  },
  beforeDestroy() {
    this.disposed = true; this.cancelSource.cancel(); if (this.panzoom) this.panzoom.destroy();
    window.removeEventListener('resize', this.updateViewportSpace);
    window.removeEventListener('keydown', this.exitFullWindowOnEscape);
    if (this.fullWindow) document.body.style.overflow = this.previousBodyOverflow;
    window.clearInterval(this.dayTimer);
  },
  methods: {
    toggleFullWindow() {
      this.clearHoveredDesk(this.hoveredId);
      this.legendOpen = false; this.searchOpen = false;
      if (!this.fullWindow) {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else document.body.style.overflow = this.previousBodyOverflow;
      this.fullWindow = !this.fullWindow;
      this.$nextTick(() => { if (!this.fullWindow) this.updateViewportSpace(); });
    },
    exitFullWindowOnEscape(event) {
      if (event.key !== 'Escape' || event.defaultPrevented || !this.fullWindow) return;
      event.preventDefault(); this.toggleFullWindow();
      this.$refs.windowToggle.$el.focus({ preventScroll: true });
    },
    isDeskDimmed(entry) { return entry.key !== this.selectedId && entry.registered && !this.matchingIds.has(entry.key); },
    updateViewportSpace() {
      this.clearHoveredDesk(this.hoveredId);
      const top = this.$el.getBoundingClientRect().top + window.scrollY;
      const footer = this.$vuetify.application.footer + this.$vuetify.application.bottom;
      this.$el.style.setProperty('--plan-viewport-offset', `${Math.ceil(top + footer + 24)}px`);
    },
    submitSearch() { this.searchIndex = 0; this.searchOpen = this.searchActive; this.$refs.searchField.focus(); },
    closeSearch() { this.searchOpen = false; },
    hoverDesk(entry, event) {
      if (event.pointerType !== 'mouse' || event.buttons || window.matchMedia('(max-width: 900px)').matches) return;
      if (entry.key === this.suppressedHoverId) return;
      this.suppressedHoverId = '';
      const bounds = event.currentTarget.getBoundingClientRect();
      this.hoveredId = entry.key; this.hoverPoint = { x: bounds.right, y: bounds.top };
    },
    clearHoveredDesk(id, event) {
      if (event && this.mobilePreview) return;
      if (this.hoveredId === id) { this.hoveredId = ''; this.hoverPoint = null; this.mobilePreview = false; }
      if (event && this.suppressedHoverId === id) {
        const bounds = event.currentTarget.getBoundingClientRect();
        // Moving a selected desk to its SVG layer can emit leave/enter without the mouse leaving it.
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.suppressedHoverId = '';
      }
    },
    chooseSearchResult(entry) { this.closeSearch(); this.selectDesk(entry, true); },
    navigateSearch(event) {
      if (event.key === 'Escape') { this.closeSearch(); return; }
      if (!this.showSearchResults || !this.searchResults.length) return;
      if (['ArrowUp', 'ArrowDown'].includes(event.key)) { event.preventDefault(); this.searchIndex = (this.searchIndex + (event.key === 'ArrowDown' ? 1 : -1) + this.searchResults.length) % this.searchResults.length; }
      if (event.key === 'Enter') { event.preventDefault(); this.chooseSearchResult(this.searchResults[Math.min(this.searchIndex, this.searchResults.length - 1)]); }
    },
    regionName(region) { return this.$t('building.plan.regions.' + region); },
    color(workplace) { return deskColor(workplace, this.isAdmin); },
    deskLabel(workplace) {
      const parts = [workplace.place_code, ...workplace.active.map(assignment => assignment.name), this.$t('building.plan.states.' + workplace.state)];
      if (this.isAdmin && workplace.photoPending) parts.push(this.$t('building.plan.photoPending') + ': ' + workplace.photoPending);
      if (this.isAdmin && workplace.bioPending) parts.push(this.$t('building.plan.bioPending') + ': ' + workplace.bioPending);
      return parts.join(' · ');
    },
    async load() {
      this.loading = true; this.error = false;
      const tasks = [this.loadWorkplaces()];
      if (!this.geometry.geometries.length) tasks.push(this.loadGeometry());
      await Promise.all(tasks);
      if (this.disposed) return;
      this.loading = false; this.initialized = true;
      await this.$nextTick();
      if (!this.panzoom && this.geometry.geometries.length) this.panzoom = Panzoom(this.$refs.map.$el, { maxScale: 10, minScale: 1, contain: 'outside', origin: '50% 50%' });
      this.syncRoute();
    },
    async loadGeometry() {
      this.geometryError = false;
      try {
        const response = await axios.get(require('@/assets/plan.svg'), { cancelToken: this.cancelSource.token });
        if (!this.disposed) this.geometry = readPlanGeometry(response.data);
      } catch (error) { if (!axios.isCancel(error)) { this.geometryError = true; this.viewMode = 'list'; } }
    },
    async loadWorkplaces() {
      try {
        const response = await axios.get(this.endpoint + '/workplace', { cancelToken: this.cancelSource.token });
        if (!Array.isArray(response.data)) throw new Error('Invalid workplace response');
        if (this.disposed) return;
        this.rawWorkplaces = response.data;
        if (!this.isAdmin) await this.loadOwnComputer();
      } catch (error) { if (!axios.isCancel(error) && !this.disposed) this.error = true; }
    },
    async loadOwnComputer() {
      const personId = this.$store.getters.user.person;
      if (!personId) return;
      const ownDesks = this.rawWorkplaces.filter(workplace => (workplace.workplace_assignations || []).some(assignment => isActiveAssignmentForPerson(assignment, personId)));
      await Promise.all(ownDesks.map(async workplace => {
        try {
          const response = await axios.get(this.endpoint + '/workplace/' + workplace.id, { cancelToken: this.cancelSource.token });
          if (!this.disposed) this.$set(workplace, 'host', response.data.host);
        } catch (error) { /* The public list remains usable when own-computer details are unavailable. */ }
      }));
    },
    syncRoute() {
      const previousRegion = this.region;
      const queryRegion = this.$route.query.floor;
      this.region = this.regionOptions.some(option => option.value === queryRegion) ? queryRegion : 'all';
      let hash;
      try { hash = decodeURIComponent(this.$route.hash.slice(1)); } catch (error) { hash = this.$route.hash.slice(1); }
      const id = deskKey(hash);
      const entry = this.workplacesById[id] || this.mapEntries.find(workplace => workplace.key === id);
      this.missingHash = hash && !entry ? hash : ''; this.selectedId = entry ? id : '';
      if (entry && (!queryRegion || !isInRegion(this.region, entry.region))) this.region = entry.region;
      if (entry && !entry.geometry) this.viewMode = 'list';
      const expandMap = Boolean(entry && this.viewMode === 'map' && this.canExpandToDetail(this.region));
      this.mapHeightExpanded = expandMap;
      if (previousRegion !== this.region) this.$nextTick(() => {
        if (!this.panzoom) return;
        this.panzoom.reset({ animate: false });
      });
    },
    routeSelection(replace = false) {
      const route = { path: this.$route.path, query: { ...this.$route.query, floor: this.region }, hash: this.selectedId ? '#' + this.selectedId : '' };
      if (this.$router.resolve(route).route.fullPath === this.$route.fullPath) return;
      this.$router[replace ? 'replace' : 'push'](route).catch(error => { if (error.name !== 'NavigationDuplicated') throw error; });
    },
    changeRegion() {
      if (this.selectedWorkplace && !isInRegion(this.region, this.selectedWorkplace.region)) this.selectedId = '';
      this.mapHeightExpanded = false;
      this.routeSelection(true); this.$nextTick(() => { if (this.panzoom) this.panzoom.reset({ animate: false }); });
    },
    selectDesk(workplace, fromList = false) {
      const previousRegion = this.region;
      const previousId = this.selectedId;
      const wasExpanded = this.mapHeightExpanded;
      this.selectedId = workplace.key; this.missingHash = ''; if (fromList) this.region = workplace.region;
      this.mapHeightExpanded = this.viewMode === 'map' && workplace.geometry && this.canExpandToDetail(this.region);
      this.routeSelection();
      this.$nextTick(() => {
        if (fromList && this.panzoom && (previousRegion !== this.region || wasExpanded !== this.mapHeightExpanded || previousId !== this.selectedId)) this.panzoom.reset({ animate: false });
        if (window.matchMedia('(max-width: 900px)').matches) this.$el.querySelector('.workplace-detail').scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    selectFromMap(workplace, event) {
      if (event.type === 'click' && this.pointerDragged) return;
      if (event.type === 'click' && window.matchMedia('(max-width: 900px)').matches) {
        const bounds = event.currentTarget.getBoundingClientRect();
        this.mobilePreview = true; this.hoveredId = workplace.key;
        this.hoverPoint = { x: bounds.right, y: bounds.top };
        return;
      }
      this.suppressedHoverId = workplace.key; this.clearHoveredDesk(this.hoveredId);
      this.selectDesk(workplace); this.$refs.map.$el.focus({ preventScroll: true });
    },
    openPreviewDesk(workplace) { this.clearHoveredDesk(this.hoveredId); this.selectDesk(workplace); },
    clearSelection() {
      this.selectedId = ''; this.missingHash = ''; this.mapHeightExpanded = false; this.routeSelection();
      this.$nextTick(() => { if (this.panzoom) this.panzoom.reset({ animate: false }); });
    },
    canExpandToDetail(region) { return ['second', 'first', 'ground', 'basement'].includes(region) && window.matchMedia('(min-width: 901px)').matches; },
    focusSelected() {
      if (!this.selectedWorkplace || !this.selectedWorkplace.geometry) return;
      this.clearHoveredDesk(this.hoveredId);
      const selectedId = this.selectedId;
      this.region = this.selectedWorkplace.region; this.viewMode = 'map'; this.routeSelection();
      const region = this.region;
      this.mapHeightExpanded = this.canExpandToDetail(this.region);
      this.$nextTick(() => {
        if (!this.panzoom) return;
        const bounds = PLAN_REGIONS.find(region => region.id === this.region).bounds;
        const stage = this.$refs.stage, center = this.selectedWorkplace.geometry.center;
        const stageWidth = stage.clientWidth, stageHeight = stage.clientHeight;
        const baseUnit = Math.min(stageWidth / bounds[2], stageHeight / bounds[3]);
        const zoomScale = this.mapHeightExpanded
          ? Math.max(stageWidth / bounds[2], stageHeight / bounds[3]) / baseUnit
          : 3;
        const pointX = (stageWidth - bounds[2] * baseUnit) / 2 + (center.x - bounds[0]) * baseUnit;
        const pointY = (stageHeight - bounds[3] * baseUnit) / 2 + (center.y - bounds[1]) * baseUnit;
        this.panzoom.reset({ animate: false });
        // Panzoom applies transforms on animation frames; containment needs the updated dimensions.
        window.requestAnimationFrame(() => {
          if (this.disposed || this.selectedId !== selectedId || this.region !== region) return;
          this.panzoom.zoom(zoomScale, { animate: false });
          window.requestAnimationFrame(() => {
            if (this.disposed || this.selectedId !== selectedId || this.region !== region) return;
            this.panzoom.pan(stageWidth / 2 - pointX, stageHeight / 2 - pointY, { animate: false });
            this.$refs.map.$el.focus({ preventScroll: true });
          });
        });
      });
    },
    mapAction(action) {
      if (!this.panzoom) return;
      this.clearHoveredDesk(this.hoveredId);
      if (action === 'fit') {
        this.mapHeightExpanded = false;
        this.$nextTick(() => this.panzoom && this.panzoom.reset({ animate: false }));
      } else if (action === 'in') this.panzoom.zoomIn();
      else this.panzoom.zoomOut();
    },
    zoomWheel(event) { this.clearHoveredDesk(this.hoveredId); if (this.panzoom) this.panzoom.zoomWithWheel(event); },
    pointerDown(event) {
      if (event.target.closest('.desk-preview')) return;
      this.clearHoveredDesk(this.hoveredId); this.pointerOrigin = { x: event.clientX, y: event.clientY }; this.pointerDragged = false;
    },
    pointerMove(event) { if (event.buttons && this.pointerOrigin && Math.hypot(event.clientX - this.pointerOrigin.x, event.clientY - this.pointerOrigin.y) > 5) this.pointerDragged = true; },
    navigateMap(event) {
      if (event.key === 'Escape' && this.fullWindow) { this.exitFullWindowOnEscape(event); return; }
      if (event.key === 'Escape') { event.preventDefault(); this.clearSelection(); return; }
      const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }, direction = directions[event.key];
      if (!direction) return;
      event.preventDefault(); const current = this.selectedWorkplace;
      if (!current || !current.geometry) { const first = this.visibleMapEntries.find(entry => this.matchingIds.has(entry.key)); if (first) this.selectDesk(first); return; }
      const candidates = this.mapEntries.filter(entry => entry.key !== current.key && entry.region === current.region && this.matchingIds.has(entry.key)).map(entry => {
        const dx = entry.geometry.center.x - current.geometry.center.x, dy = entry.geometry.center.y - current.geometry.center.y;
        return { entry, forward: dx * direction[0] + dy * direction[1], score: Math.hypot(dx, dy) + Math.abs(dx * direction[1] - dy * direction[0]) * 2 };
      }).filter(candidate => candidate.forward > 1).sort((a, b) => a.score - b.score);
      if (candidates.length) this.selectDesk(candidates[0].entry);
    },
    async copyText(text) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(text);
        else {
          const field = document.createElement('textarea'); field.value = text; field.style.cssText = 'position:fixed;left:-9999px'; document.body.appendChild(field); field.select();
          const copied = document.execCommand('copy'); field.remove(); if (!copied) throw new Error('Clipboard unavailable');
        }
        this.copyNotice = true;
      } catch (error) { this.$store.dispatch('alert', { text: this.$t('building.plan.copyError'), color: 'error' }); }
    },
    copyLink() { const href = this.$router.resolve({ path: '/building', query: { floor: this.region }, hash: '#' + this.selectedId }).href; this.copyText(new URL(href, window.location.origin).href); }
  }
};
</script>

<style scoped>
.building-plan { text-align: left; background: #fff; border-radius: 6px; letter-spacing: 0; }
.building-plan > .v-alert { margin: 12px 16px; }
.plan-toolbar { display: grid; grid-template-columns: minmax(220px, 280px) minmax(180px, 210px) minmax(180px, 1fr) 80px auto 40px; align-items: center; gap: 10px; padding: 16px; }
.plan-window-toggle { width: 40px; height: 40px; }
.building-plan.building-plan--full-window { position: fixed; inset: 0; z-index: 200; width: 100%; height: 100vh; height: 100dvh; min-height: 0; border: 0; border-radius: 0; display: flex; flex-direction: column; overflow: hidden; }
.building-plan--full-window .plan-toolbar { flex-shrink: 0; }
.building-plan--full-window .plan-layout { flex: 1 1 0; min-height: 0; border-radius: 0; }
.plan-location, .plan-status { min-width: 0; }
.plan-search { position: relative; display: flex; align-items: center; gap: 0; min-width: 0; height: 40px; box-sizing: border-box; border: 1px solid #9eaaae; border-radius: 4px; background: #fff; }
.plan-search:focus-within { border-color: #ec651e; }
.plan-search > .v-input { flex: 1 1 auto; min-width: 0; height: 38px; margin-top: 0; }
.plan-search ::v-deep fieldset { display: none; }
.plan-search ::v-deep .v-input__control, .plan-search ::v-deep .v-input__slot { height: 38px; min-height: 38px; margin-bottom: 0; }
.plan-search ::v-deep .v-input__slot { padding: 0 12px; }
.building-plan .plan-toolbar ::v-deep .v-label { top: 10px; height: 20px; line-height: 20px; font-size: 14px; white-space: nowrap; }
.plan-search ::v-deep input { margin: 0; padding: 0; height: 38px; max-height: 38px; font-size: 14px; }
.building-plan .plan-search ::v-deep .v-input__prepend-inner,
.building-plan .plan-search ::v-deep .v-input__append-inner { margin-top: 0 !important; padding-top: 0; align-self: center; }
.plan-search ::v-deep .v-input__icon .v-icon { margin-top: 0 !important; }
.plan-toolbar ::v-deep .v-select__selection { font-size: 14px; }
.plan-search-button { flex: 0 0 38px; width: 38px; min-width: 38px; height: 38px; border-left: 1px solid #dfe3e4; border-radius: 0 3px 3px 0; }
.plan-search-results { position: absolute; z-index: 6; top: 100%; left: 0; right: 0; margin: 4px 0 0; padding: 0; list-style: none; max-height: 330px; overflow: auto; background: white; border: 1px solid #dfe3e4; box-shadow: 0 4px 12px #0002; border-radius: 4px; }
.plan-search-results button { width: 100%; padding: 10px 12px; text-align: left; font-size: 13px; overflow-wrap: anywhere; }
.plan-search-results li[aria-selected="true"] { background: #f0f4f4; }
.plan-search-results small { margin-left: 8px; color: #637076; }
.plan-search-results span { display: block; }
.plan-search-empty { padding: 12px; font-size: 13px; }
.plan-view-toggle { width: 80px; }
.plan-view-toggle .v-btn { height: 40px !important; min-width: 40px !important; padding: 0 10px !important; }
.plan-result-count { font-size: 12px; color: #637076; justify-self: end; white-space: nowrap; }
.plan-layout { display: grid; grid-template-columns: minmax(0, 1fr) 330px; border-top: 1px solid #e8ebec; border-radius: 0 0 6px 6px; overflow: hidden; }
.plan-main { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
.plan-layout > .workplace-detail { max-height: 900px; }
.plan-map-area { position: relative; background: #fff; }
.plan-map-tools, .plan-legend-tool { position: absolute; right: 12px; z-index: 2; display: flex; align-items: center; padding: 0; gap: 4px; }
.plan-map-tools { top: 12px; }
.plan-legend-tool { bottom: 12px; }
.plan-map-tools .v-btn, .plan-legend-tool .v-btn { color: #4f5d63; background-color: transparent !important; box-shadow: none !important; text-shadow: 0 1px 2px #fff; }
.plan-map-tools .v-btn::before, .plan-legend-tool .v-btn::before { background-color: transparent !important; }
.plan-map-stage { position: relative; overflow: hidden; width: 100%; max-width: 100%; height: auto; aspect-ratio: var(--plan-aspect); min-height: 280px; max-height: 620px; }
.plan-map-stage--building { max-height: 820px; }
@media (min-width: 901px) {
  .building-plan { display: flex; flex-direction: column; min-height: calc(100vh - var(--plan-viewport-offset, 240px)); min-height: calc(100dvh - var(--plan-viewport-offset, 240px)); }
  .plan-layout { flex: 1 1 auto; }
  .plan-layout > .workplace-detail { contain: size; max-height: none; }
  .plan-map-area { display: flex; flex: 1 1 auto; flex-direction: column; min-height: 0; }
  .plan-map-stage { flex: 1 1 0; aspect-ratio: auto; max-height: none; }
  .plan-list-area { position: relative; flex: 1 1 0; min-height: 280px; }
  .plan-list-area > .v-progress-linear { position: absolute; top: 0; z-index: 3; }
  .plan-results-table { position: absolute; inset: 0; height: 100%; }
  .plan-results-table ::v-deep .v-data-table__wrapper { height: 100% !important; }
}
.plan-loading { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: #ffffffbb; }
.plan-map-empty { position: absolute; bottom: 12px; left: 12px; max-width: calc(100% - 76px); padding: 8px 12px; background: #ffffffed; border: 1px solid #dfe3e4; border-radius: 4px; font-size: 13px; }
.plan-legend { display: grid; gap: 8px; padding: 16px; max-height: min(65vh, 480px); overflow-y: auto; background: white; }
.plan-legend h3, .plan-legend h4 { margin: 0; font-size: 13px; font-weight: 600; color: #343b3d; }
.plan-legend h3 { padding-left: 0; position: static; }
.plan-legend h3::before { display: none; }
.plan-legend span { font-size: 12px; color: #4f5d63; display: inline-flex; align-items: center; gap: 8px; }
.plan-legend i { flex-shrink: 0; }
.plan-legend i, .result-status-dot { display: inline-block; width: 11px; height: 11px; border: 1px solid #879298; border-radius: 2px; }
.legend-shared { display: inline-flex; align-items: center; justify-content: center; width: 14px; height: 14px; flex: 0 0 14px; border-radius: 50%; background: #343b3d; color: white; font-size: 10px; font-weight: normal; font-family: Arial, sans-serif; }
.plan-expiry-legend { display: grid; gap: 8px; padding-top: 8px; }
.legend-dot { display: inline-block; width: 7px !important; height: 7px !important; border: 0 !important; border-radius: 50% !important; }
.plan-results-table th, .plan-results-table td { white-space: normal; }
.plan-results-table tr { cursor: pointer; }
.plan-results-table td { font-size: 12px; padding-top: 10px !important; padding-bottom: 10px !important; overflow-wrap: anywhere; }
.plan-results-table td:first-child { min-width: 95px; }
.plan-results-table small { display: block; color: #637076; font-size: 11px; }
.result-person { display: block; }
.result-status-dot { margin-right: 5px; vertical-align: middle; }
.result-pending { display: inline-flex; align-items: center; gap: 4px; margin-right: 8px; }
.plan-result--selected { background: #fff1e8; }
.plan-results-table tr:focus { outline: 2px solid #ec651e; outline-offset: -2px; }
.plan-list-empty { text-align: center; padding: 40px !important; }
@media (max-width: 1100px) { .plan-layout { grid-template-columns: minmax(0, 1fr) 300px; } }
@media (min-width: 901px) and (max-width: 1100px) {
  .plan-toolbar { grid-template-columns: minmax(160px, 220px) minmax(140px, 180px) minmax(120px, 1fr) 80px auto 40px; }
}
@media (max-width: 900px) {
  .plan-toolbar { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto; }
  .plan-location { grid-column: 1 / 3; grid-row: 1; }
  .plan-status { grid-column: 1 / 3; grid-row: 2; }
  .plan-search { grid-column: 1 / -1; grid-row: 3; }
  .plan-view-toggle { grid-column: 3; grid-row: 1; }
  .plan-result-count { grid-column: 3; grid-row: 2; }
  .plan-window-toggle { grid-column: 3; grid-row: 3; justify-self: end; }
  .plan-search { grid-column: 1 / 3; }
  .building-plan--full-window .plan-layout { overflow-y: auto; }
  .plan-layout { display: flex; flex-direction: column; }
  .plan-main { display: block; flex: 0 0 auto; }
  .plan-map-stage { max-height: 520px; }
  .plan-map-stage--building { max-height: 660px; }
  .plan-map-stage--expanded { aspect-ratio: var(--plan-aspect); min-height: 280px; }
  .plan-layout > .workplace-detail { flex: 0 0 auto; max-height: none; overflow-y: visible; }
}
@media (max-width: 600px) {
  .plan-toolbar { gap: 8px; padding: 12px; }
  .plan-result-count { font-size: 11px; }
  .plan-map-stage { max-height: 440px; }
  .plan-map-stage--building { max-height: 580px; }
}
</style>
